import React from 'react';
import { Helmet } from 'react-helmet';
import { Tabs, Switch } from 'antd';
import { injectIntl } from 'react-intl';
import Authorize from 'components/Providers/Authorize';
import PageWrapper from 'components/Shared/Page/Wrapper';
import PageTitle from 'components/Shared/Page/Title';
import SearchInput from 'components/Shared/Search';
import withPusher from 'components/Providers/Pusher/withPusher';
import PageBanners from 'components/Layout/PageBanners';
import useTranslation from 'utils/hooks/useTranslation';
import FundingRequestsEmptyState from './FundingRequestsEmptyState';
import FundingRequestsTable from './FundingRequestsTable';
import FundingRequestReviewModal from './FundingRequestReviewModal';
import { getFundingRequestsMockData } from './mockData';
import useFundingRequestsPage from './useFundingRequestsPage';

import styles from './style.module.scss';

const { TabPane } = Tabs;

const FUNDING_REQUEST_TABS = ['pending', 'approved', 'rejected', 'all'];

const FUNDING_REQUEST_TAB_LABEL_KEYS = {
  pending: 'corporateAccounts.common.status.pending',
  approved: 'corporateAccounts.fundingRequests.status.approved',
  rejected: 'corporateAccounts.fundingRequests.status.rejected',
  all: 'corporateAccounts.common.all',
};

const FundingRequests = ({ history }) => {
  const { translateText } = useTranslation();
  const {
    searchValue,
    activeTab,
    reviewingRequest,
    showData,
    isApproving,
    isDeclining,
    onSearch,
    onTabChange,
    onReview,
    onCloseReview,
    onToggleShowData,
    onApprove,
    onDecline,
  } = useFundingRequestsPage();

  const searchBar = (
    <SearchInput
      className={styles.searchInput}
      placeholder={translateText('corporateAccounts.fundingRequests.searchPlaceholder')}
      searchValue={searchValue}
      onSearchFunc={onSearch}
    />
  );

  return (
    <Authorize>
      <Helmet title="Funding requests" />
      <PageBanners />
      <PageWrapper history={history} loading={false}>
        <PageTitle
          title={translateText('corporateAccounts.fundingRequests.title')}
          description={translateText('corporateAccounts.fundingRequests.description')}
        />
        <div className="card-body">
          {/* TEMPORARY dev toggle — remove once real funding request data exists. */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}
            data-testid="fundingRequestsStateToggle"
          >
            <Switch checked={showData} onChange={onToggleShowData} size="small" />
            <span>
              {showData
                ? 'TEMPORARY dev toggle Showing: Mock data'
                : 'TEMPORARY dev toggle Showing: No data'}
            </span>
          </div>

          <div className={styles.tabsWrapper}>
            <Tabs activeKey={activeTab} onChange={onTabChange} tabBarExtraContent={searchBar}>
              {FUNDING_REQUEST_TABS.map(tabKey => {
                // Only the Pending tab carries a count (the number of requests
                // awaiting review). It is always present so tab widths stay stable
                // — green when Pending is the open tab, muted otherwise.
                const pendingCount = showData ? getFundingRequestsMockData('pending').length : 0;
                const showCount = tabKey === 'pending' && pendingCount > 0;

                return (
                  <TabPane
                    tab={
                      <span className={styles.tabLabel}>
                        {translateText(FUNDING_REQUEST_TAB_LABEL_KEYS[tabKey])}
                        {showCount && (
                          <span
                            className={`${styles.tabCount} ${
                              activeTab === 'pending' ? styles.tabCountActive : styles.tabCountMuted
                            }`}
                          >
                            {pendingCount}
                          </span>
                        )}
                      </span>
                    }
                    key={tabKey}
                  >
                    {showData ? (
                      <FundingRequestsTable status={tabKey} onReview={onReview} />
                    ) : (
                      <FundingRequestsEmptyState />
                    )}
                  </TabPane>
                );
              })}
            </Tabs>
          </div>
        </div>
      </PageWrapper>
      <FundingRequestReviewModal
        visible={!!reviewingRequest}
        fundingRequest={reviewingRequest}
        onClose={onCloseReview}
        onApprove={onApprove}
        onDecline={onDecline}
        isApproving={isApproving}
        isDeclining={isDeclining}
      />
    </Authorize>
  );
};

export default withPusher(injectIntl(FundingRequests));
